/**
 * Notifications Queue — the operator's primary workspace for the
 * Editorial Email Loop. Every send is gated through this page; the
 * promote-hook only creates DRAFTS, never sends.
 *
 * Top-level view: pending-approval drafts list.
 * Drawer view (click a row): full draft drawer with:
 *   • Bidirectional recipient table (checkboxes; hard-locked disabled,
 *     soft-locked toggleable with operator_override flag)
 *   • Email preview tabs (Full HTML / Images blocked / No CSS / Plain text)
 *   • Headers preview
 *   • Action bar: Approve & Send / Reject / Cancel
 *
 * Test-mode awareness: when the target has `test_mode_enabled=true`, the
 * approve button changes label to "Approve & send to tester" and a
 * banner explains the rewrite.
 */

import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  RefreshCw, AlertTriangle, CheckCircle2, X, Mail, ArrowRight, Clock,
  Eye, Code, Image as ImageIcon, FileText, Send, ShieldAlert, Settings,
  Power, PowerOff, Search,
  Edit2, Trash2, Plus, UserPlus,
} from 'lucide-react';
import {
  listQueue, listQueueByEntityDay, getDraft, approveAndSendDraft, approveRecipients, sendDraft, rejectDraft, cancelDraft, listRecipientReviews,
  scheduleDraft,
  isHardLocked, isSoftLocked, explainReason,
  getSystemConfig, setSystemEnabled,
  listTargets, setTargetEnabled,
  patchRecipient, deleteRecipient, updateDraftContent,
  type DraftSummary, type DraftDetail, type DraftStatus, type FinalRecipient,
  type TargetOverview, type DraftRecipient,
} from '../lib/api/notifications.js';
import { getTargetNotificationSettings } from '../lib/api/notifications.js';
import { EntityGroupFilter, useEntityGroupPill } from '../components/EntityGroupFilter.js';
import { PublicationFilter, usePublicationPill } from '../components/PublicationFilter.js';
import { AddRecipientModal } from '../components/AddRecipientModal.js';
import { EntityDayGroup } from '../components/notifications/EntityDayGroup.js';
import { CoverageBadges } from '../components/CoverageBadges.js';
import { ScheduledCalendarView } from '../components/notifications/ScheduledCalendarView.js';
import { ScheduleSendPopover, tomorrowAtSydney } from '../components/notifications/ScheduleSendPopover.js';
import { handleMutationConflict } from '../lib/mutationConflict.js';
import { useWebSocket } from '../hooks/useWebSocket.js';
import { usePresence } from '../hooks/usePresence.js';
import { PresencePill } from '../components/PresencePill.js';
import { DateBar } from '../components/DateBar.js';
import { TableScroll } from '../components/ui/table-scroll.js';

type ViewMode = 'grouped' | 'flat' | 'calendar';

type PreviewMode = 'full' | 'images_blocked' | 'no_css' | 'plain_text';

const PREVIEW_TABS: Array<{ id: PreviewMode; label: string; icon: typeof Eye }> = [
  { id: 'full', label: 'Full HTML', icon: Eye },
  { id: 'images_blocked', label: 'Images blocked', icon: ImageIcon },
  { id: 'no_css', label: 'No CSS', icon: FileText },
  { id: 'plain_text', label: 'Plain text', icon: Code },
];

// Status filter for the queue list. Mirrors social's Drafts/Posted/Skipped
// three-bucket pattern. Maps multiple terminal statuses into "Cancelled"
// because operators don't typically need to distinguish reject/cancel/expire
// in the daily flow.
type QueueFilter = 'pending_approval' | 'sent' | 'cancelled';

const STATUS_OPTIONS: { value: QueueFilter; label: string; serverStatuses: DraftStatus[] }[] = [
  { value: 'pending_approval', label: 'Drafts',    serverStatuses: ['pending_approval'] },
  { value: 'sent',             label: 'Sent',      serverStatuses: ['sent'] },
  // 'Hidden' bucket = cancelled OR rejected OR expired (terminal not-sent).
  // QueueFilter value stays 'cancelled' to avoid churning the local-storage
  // remembered selection; only the operator-facing label changes.
  { value: 'cancelled',        label: 'Hidden',    serverStatuses: ['cancelled', 'rejected', 'expired'] },
];

export default function NotificationsQueuePage() {
  const [selectedDraftId, setSelectedDraftId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<QueueFilter>('pending_approval');
  const [anchorDate, setAnchorDate] = useState<string>(todayYMD());
  const [selectedDate, setSelectedDate] = useState<string>(todayYMD());
  const [searchInput, setSearchInput] = useState('');
  // mig 173 — three-view tab: Grouped (default, entity-day) / Flat (legacy
  // per-draft list) / Calendar (week+month of scheduled sends).
  const [viewMode, setViewMode] = useState<ViewMode>('grouped');
  const qc = useQueryClient();

  const debouncedSearch = useDebounced(searchInput.trim(), 300);
  const searchActive = debouncedSearch.length > 0;

  // Realtime: subscribe to the global notifications channel. The backend
  // emits draft_status_changed at every mutation endpoint (and the
  // publishing-service emits on Mailjet cascade completion). The
  // WebSocketContext auto-invalidates `['notifications']` on receipt
  // (parts[1] of channel literal — see WebSocketContext.tsx:175), which
  // covers all of this page's React Query keys. The 5-min poll below is a
  // safety net for cases where WS is briefly disconnected.
  useWebSocket({ channels: ['assayerai:notifications'] });

  // Entity-group pill (sticky across all four list pages via localStorage).
  const [entityGroup] = useEntityGroupPill();
  const entityGroupParam = entityGroup === 'all' ? undefined : entityGroup;

  const days = useMemo(() => {
    // 7-day strip ending on anchorDate, latest-on-right (Social pattern).
    const out: string[] = [];
    for (let i = 6; i >= 0; i--) out.push(shiftDays(anchorDate, -i));
    return out;
  }, [anchorDate]);

  const { targetId: publicationTargetId } = usePublicationPill();

  const queryKey = searchActive
    ? ['notifications', 'queue', 'search', debouncedSearch, entityGroup, publicationTargetId]
    : ['notifications', 'queue', 'date', selectedDate, statusFilter, entityGroup, publicationTargetId];

  // Backend filters by a single status string; we fetch each member of the
  // bucket separately when the bucket maps to multiple statuses (only
  // 'cancelled' does today). Fewer round-trips than client-side filtering
  // would seem to be — but the cancelled bucket is small so this is fine.
  const { data, isLoading, error, refetch, isRefetching } = useQuery({
    queryKey,
    queryFn: async () => {
      const targetParam = publicationTargetId ?? undefined;
      if (searchActive) {
        return listQueue({ q: debouncedSearch, status: 'all', entity_group: entityGroupParam, target_id: targetParam, limit: 200 });
      }
      const bucket = STATUS_OPTIONS.find(o => o.value === statusFilter)!;
      // Single-status bucket → one fetch.
      if (bucket.serverStatuses.length === 1) {
        return listQueue({
          status: bucket.serverStatuses[0],
          date: selectedDate,
          entity_group: entityGroupParam,
          target_id: targetParam,
          limit: 200,
        });
      }
      // Multi-status bucket — fan out, concat, re-sort by created_at DESC.
      const responses = await Promise.all(
        bucket.serverStatuses.map(s =>
          listQueue({ status: s, date: selectedDate, entity_group: entityGroupParam, target_id: targetParam, limit: 200 }),
        ),
      );
      const items = responses.flatMap(r => r.items);
      items.sort((a, b) => b.created_at.localeCompare(a.created_at));
      return { items, count: items.length, limit: 200, offset: 0 };
    },
    // WS push is primary; this poll is a safety net for transient WS drops.
    refetchInterval: 5 * 60_000,
  });

  // Grouped view — per-entity-day collapsed list. Only fetched when the
  // Grouped tab is active (calendar has its own query inside the component).
  const groupedQuery = useQuery({
    queryKey: [
      'notifications', 'queue-grouped', selectedDate, statusFilter,
      entityGroup, publicationTargetId, debouncedSearch,
    ],
    queryFn: async () => {
      const targetParam = publicationTargetId ?? undefined;
      const bucket = STATUS_OPTIONS.find(o => o.value === statusFilter)!;
      // For Grouped v1, single-status buckets only (matches the bucket-1
      // case in the Flat query above). Multi-status buckets (Hidden) fall
      // back to fetching the first status; operators rarely group those.
      const status = searchActive ? 'all' : bucket.serverStatuses[0];
      return listQueueByEntityDay({
        status: status as DraftStatus | 'all',
        date: searchActive ? undefined : selectedDate,
        q: searchActive ? debouncedSearch : undefined,
        entity_group: entityGroupParam,
        target_id: targetParam,
        limit: 500,
      });
    },
    enabled: viewMode === 'grouped',
    // WS push is primary; this poll is a safety net for transient WS drops.
    refetchInterval: 5 * 60_000,
  });

  const systemConfigQuery = useQuery({
    queryKey: ['notifications', 'system-config'],
    queryFn: getSystemConfig,
  });

  const targetsQuery = useQuery({
    queryKey: ['notifications', 'targets-overview'],
    queryFn: listTargets,
  });

  // Pending-reviews count — drives the badge on the "Recipient reviews"
  // header link. Backend returns count = len(items) capped at the limit, so
  // values are accurate up to ~500. Bigger backlogs are operationally a
  // problem in themselves and the badge truncating to "500+" is informative.
  const pendingReviewsQuery = useQuery({
    queryKey: ['notifications', 'recipient-reviews', 'pending-count'],
    queryFn: () => listRecipientReviews({ status: 'pending', limit: 500 }),
    refetchInterval: 60_000,
  });
  const pendingReviewsCount = pendingReviewsQuery.data?.count ?? 0;

  const systemToggleMutation = useMutation({
    mutationFn: (enabled: boolean) => setSystemEnabled(enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications', 'system-config'] }),
  });

  const targetToggleMutation = useMutation({
    mutationFn: ({ targetId, enabled }: { targetId: string; enabled: boolean }) =>
      setTargetEnabled(targetId, enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications', 'targets-overview'] }),
  });

  const systemEnabled = systemConfigQuery.data?.notifications_enabled === true;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <header className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-grey-900 flex items-center gap-2">
            <Mail className="h-6 w-6" />
            Notifications Queue
          </h1>
          <p className="text-sm text-grey-600 mt-1">
            Media-contact notification drafts pending operator approval. <strong>Every send requires your explicit Approve click</strong> — nothing reaches a journalist without it. There is no auto-send path.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Link
            to="/notifications/recipient-reviews"
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-grey-300 rounded hover:bg-grey-50"
            title="Cross-article trust ledger — approve / reject (recipient, entity) pairs in advance."
          >
            Recipient reviews
            {pendingReviewsCount > 0 && (
              <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-warning-100 text-warning-800 tabular-nums">
                {pendingReviewsCount}
              </span>
            )}
          </Link>
          <button
            onClick={() => refetch()}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-grey-300 rounded hover:bg-grey-50"
          >
            <RefreshCw className={`h-4 w-4 ${isRefetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </header>

      {/* System-wide kill switch */}
      <section className="mb-4 rounded-lg border border-grey-200 bg-surface p-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              {systemEnabled ? (
                <Power className="h-5 w-5 text-success-700" />
              ) : (
                <PowerOff className="h-5 w-5 text-grey-500" />
              )}
              <h2 className="text-base font-semibold text-grey-900">
                System {systemEnabled ? 'enabled' : 'disabled'}
              </h2>
            </div>
            <p className="text-xs text-grey-600 mt-1 max-w-2xl">
              {systemEnabled
                ? <>Promote events on any enabled target will create pending drafts. The approval-and-release flow is always in play — drafts never auto-send.</>
                : <>Promote events do not create notification drafts. Turn on to start queueing drafts for approval. Defaults to off (fail-closed).</>}
            </p>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer flex-shrink-0">
            <span className="text-sm text-grey-700">{systemEnabled ? 'On' : 'Off'}</span>
            <input
              type="checkbox"
              checked={systemEnabled}
              disabled={systemToggleMutation.isPending}
              onChange={e => systemToggleMutation.mutate(e.target.checked)}
              className="h-5 w-9 appearance-none rounded-full bg-grey-300 checked:bg-success-600 transition-colors relative cursor-pointer
                         before:content-[''] before:absolute before:top-0.5 before:left-0.5 before:h-4 before:w-4 before:rounded-full before:bg-surface before:transition-transform
                         checked:before:translate-x-4"
            />
          </label>
        </div>
      </section>

      {/* Per-pub targets list */}
      <section className="mb-6 rounded-lg border border-grey-200 bg-surface overflow-hidden">
        <div className="px-4 py-3 border-b border-grey-200 bg-grey-50">
          <h2 className="text-sm font-semibold text-grey-900">Publication targets</h2>
          <p className="text-xs text-grey-600 mt-0.5">
            Toggle per-pub notifications and review today's activity. Click Configure for sender, branding, test mode, daily cap, and legal block.
          </p>
        </div>
        {targetsQuery.isLoading && <div className="p-4 text-sm text-grey-500">Loading targets…</div>}
        {targetsQuery.data && targetsQuery.data.items.length === 0 && (
          <div className="p-4 text-sm text-grey-500">No active publication targets found.</div>
        )}
        <div className="divide-y divide-grey-100">
          {targetsQuery.data?.items.map(t => (
            <TargetRow
              key={t.target_id}
              target={t}
              systemEnabled={systemEnabled}
              onToggle={(enabled) => targetToggleMutation.mutate({ targetId: t.target_id, enabled })}
              toggling={targetToggleMutation.isPending}
            />
          ))}
        </div>
      </section>

      {/* Queue — entity-group pills + search + date selector + status tabs + cards */}
      <section>
        {/* Entity-group filter pills — sticky across all four list pages. */}
        <div className="mb-3">
          <EntityGroupFilter />
        </div>

        {/* Publication filter pills — sticky across Notifications + Social. */}
        <div className="mb-4">
          <PublicationFilter />
        </div>

        {/* Search */}
        <div className="mb-4 flex items-center gap-2">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-grey-400" />
            <input
              type="search"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Search by company code, article title, or subject…"
              className="w-full pl-9 pr-9 py-2 text-sm border border-grey-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-400"
            />
            {searchInput && (
              <button
                onClick={() => setSearchInput('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-grey-400 hover:text-grey-700"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {searchActive && (
            <span className="text-xs text-grey-500">Searching across all dates & statuses</span>
          )}
        </div>

        {/* Date bar — hidden when search is active or Calendar mode */}
        {!searchActive && viewMode !== 'calendar' && (
          <DateBar
            days={days}
            selectedDate={selectedDate}
            onSelect={setSelectedDate}
            onShiftWeek={delta => setAnchorDate(shiftDays(anchorDate, delta * 7))}
            onToday={() => {
              setAnchorDate(todayYMD());
              setSelectedDate(todayYMD());
            }}
          />
        )}

        {/* View-mode tabs: Grouped / Flat / Calendar (mig 173). */}
        <div className="flex items-center justify-end gap-1 mt-3">
          <span className="text-xs text-grey-500 mr-2">View:</span>
          <div className="inline-flex rounded border border-grey-200 overflow-hidden">
            <button
              onClick={() => setViewMode('grouped')}
              className={`px-3 py-1 text-xs ${
                viewMode === 'grouped'
                  ? 'bg-primary-04 text-white'
                  : 'bg-surface text-grey-700 hover:bg-grey-50'
              }`}
            >
              Grouped
            </button>
            <button
              onClick={() => setViewMode('flat')}
              className={`px-3 py-1 text-xs border-l border-grey-200 ${
                viewMode === 'flat'
                  ? 'bg-primary-04 text-white'
                  : 'bg-surface text-grey-700 hover:bg-grey-50'
              }`}
            >
              Flat
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`px-3 py-1 text-xs border-l border-grey-200 ${
                viewMode === 'calendar'
                  ? 'bg-primary-04 text-white'
                  : 'bg-surface text-grey-700 hover:bg-grey-50'
              }`}
            >
              Calendar
            </button>
          </div>
        </div>

        {/* Status tabs — hidden when search is active (status visible per-row instead) */}
        {!searchActive && (
          <div className="flex gap-2 mt-4 mb-4 border-b border-grey-200">
            {STATUS_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => setStatusFilter(opt.value)}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  statusFilter === opt.value
                    ? 'border-primary-600 text-primary-700'
                    : 'border-transparent text-grey-600 hover:text-grey-900'
                }`}
              >
                {opt.label}
                {statusFilter === opt.value && data?.count != null && (
                  <span className="ml-2 text-xs text-grey-500">({data.count})</span>
                )}
              </button>
            ))}
          </div>
        )}

        {error && (
          <div className="mb-4 rounded-lg border border-danger-200 bg-danger-50 p-4 text-sm text-danger-700">
            Failed to load queue: {(error as Error).message}
          </div>
        )}

        {isLoading && <div className="text-sm text-grey-500">Loading…</div>}

        {!isLoading && data && data.items.length === 0 && (
          <div className="rounded-lg border border-grey-200 bg-grey-50 p-8 text-center text-sm text-grey-600">
            {searchActive
              ? <>No drafts match <strong>"{debouncedSearch}"</strong>.</>
              : statusFilter === 'pending_approval'
                ? <>No pending drafts for {selectedDate}.{systemEnabled ? '' : ' System is disabled — turn on above to start queueing.'}</>
                : statusFilter === 'sent'
                  ? <>No sends recorded for {selectedDate}.</>
                  : <>No hidden drafts for {selectedDate}.</>}
          </div>
        )}

        {/* Render switch — Grouped (default) / Flat / Calendar. */}
        {viewMode === 'flat' && (
          <div className="space-y-2">
            {data?.items.map((d) => (
              <DraftCard
                key={d.draft_id}
                draft={d}
                showStatusBadge={searchActive || statusFilter !== 'pending_approval'}
                onOpen={() => setSelectedDraftId(d.draft_id)}
              />
            ))}
          </div>
        )}

        {viewMode === 'grouped' && (
          <div className="space-y-3">
            {groupedQuery.isLoading && (
              <div className="text-sm text-grey-500">Loading grouped queue…</div>
            )}
            {groupedQuery.data?.groups.length === 0 && !groupedQuery.isLoading && (
              <div className="rounded-lg border border-grey-200 bg-grey-50 p-8 text-center text-sm text-grey-600">
                No drafts in this view.
              </div>
            )}
            {groupedQuery.data?.groups.map((group) => (
              <EntityDayGroup
                key={(group.entity_id ?? '__sitewide__') + '-' + selectedDate}
                group={group}
                onMutated={() => {
                  qc.invalidateQueries({ queryKey: ['notifications', 'queue-grouped'] });
                  qc.invalidateQueries({ queryKey: ['notifications', 'queue'] });
                  qc.invalidateQueries({ queryKey: ['notifications-scheduled'] });
                }}
                onOpenDraft={(draftId) => setSelectedDraftId(draftId)}
              />
            ))}
          </div>
        )}

        {viewMode === 'calendar' && (
          <ScheduledCalendarView
            filters={{
              target_id: publicationTargetId ?? undefined,
              entity_group: entityGroupParam as any,
              q: debouncedSearch || undefined,
            }}
            onOpenDraft={(draftId) => setSelectedDraftId(draftId)}
          />
        )}
      </section>

      {selectedDraftId && (
        <DraftDrawer
          draftId={selectedDraftId}
          onClose={() => setSelectedDraftId(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// Target row — per-pub line in the targets list
// ---------------------------------------------------------------------

function TargetRow({
  target,
  systemEnabled,
  onToggle,
  toggling,
}: {
  target: TargetOverview;
  systemEnabled: boolean;
  onToggle: (enabled: boolean) => void;
  toggling: boolean;
}) {
  const targetEnabled = (target.config as { notifications_enabled?: boolean } | null)
    ?.notifications_enabled === true;
  const testModeOn = (target.config as { test_mode_enabled?: boolean } | null)
    ?.test_mode_enabled === true;
  const senderConfigured = !!target.notification_from_email;
  const effective = systemEnabled && targetEnabled && senderConfigured;

  let statusLabel: string;
  let statusClass: string;
  if (!senderConfigured) {
    statusLabel = 'Setup required';
    statusClass = 'bg-grey-100 text-grey-700';
  } else if (!systemEnabled) {
    statusLabel = 'System off';
    statusClass = 'bg-grey-100 text-grey-700';
  } else if (!targetEnabled) {
    statusLabel = 'Paused';
    statusClass = 'bg-grey-100 text-grey-700';
  } else if (testModeOn) {
    statusLabel = 'TEST MODE';
    statusClass = 'bg-warning-100 text-warning-800';
  } else {
    statusLabel = 'Live';
    statusClass = 'bg-success-100 text-success-800';
  }

  return (
    <div className="px-4 py-3 flex items-center gap-3">
      <label className="inline-flex items-center cursor-pointer flex-shrink-0" title={!senderConfigured ? 'Configure sender before enabling' : ''}>
        <input
          type="checkbox"
          checked={targetEnabled}
          disabled={toggling || !senderConfigured}
          onChange={e => onToggle(e.target.checked)}
          className="h-5 w-9 appearance-none rounded-full bg-grey-300 checked:bg-success-600 transition-colors relative cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed
                     before:content-[''] before:absolute before:top-0.5 before:left-0.5 before:h-4 before:w-4 before:rounded-full before:bg-surface before:transition-transform
                     checked:before:translate-x-4"
        />
      </label>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-grey-900">{target.target_name}</span>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusClass}`}>
            {effective && !testModeOn && <CheckCircle2 className="h-3 w-3" />}
            {testModeOn && <AlertTriangle className="h-3 w-3" />}
            {statusLabel}
          </span>
        </div>
        <div className="text-xs text-grey-600 mt-0.5 truncate">
          {senderConfigured
            ? <span>From: <code className="font-mono">{target.notification_from_email}</code></span>
            : <span className="text-warning-700">No sender configured</span>}
          {target.pending_draft_count > 0 && (
            <span className="ml-3 text-primary-700 font-medium">
              {target.pending_draft_count} pending draft{target.pending_draft_count === 1 ? '' : 's'}
            </span>
          )}
          {target.sent_today_count > 0 && (
            <span className="ml-3 text-success-700">
              {target.sent_today_count} sent today
            </span>
          )}
          {target.test_sent_today_count > 0 && (
            <span className="ml-3 text-warning-700">
              {target.test_sent_today_count} test-sent today
            </span>
          )}
        </div>
      </div>
      <Link
        to={`/notifications/targets/${target.target_id}`}
        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-grey-300 rounded text-grey-700 hover:bg-grey-50 flex-shrink-0"
      >
        <Settings className="h-3.5 w-3.5" />
        Configure
      </Link>
    </div>
  );
}

// ---------------------------------------------------------------------
// List row
// ---------------------------------------------------------------------

// Status badge classes by DraftStatus. Used both inline on the card
// (when status varies in the current view: search mode or non-Drafts tab)
// and structural — the bucket name to colour mapping is the canonical
// source of truth here.
const STATUS_BADGE: Record<DraftStatus, { label: string; cls: string }> = {
  pending_approval: { label: 'Pending', cls: 'bg-primary-50 text-primary-700 border border-primary-200' },
  approved:         { label: 'Approved', cls: 'bg-primary-50 text-primary-700 border border-primary-200' },
  scheduled:        { label: 'Scheduled', cls: 'bg-purple-50 text-purple-700 border border-purple-200' },
  sent:             { label: 'Sent',    cls: 'bg-success-50 text-success-700 border border-success-200' },
  // Operator-facing terminology: cancelled/rejected/expired all render as
  // "Hidden" in the per-row badge — they're indistinguishable from the
  // operator's daily view. The underlying value is preserved on the row
  // for the trust-ledger and audit trail.
  cancelled:        { label: 'Hidden',  cls: 'bg-grey-100 text-grey-700 border border-grey-200' },
  rejected:         { label: 'Hidden',  cls: 'bg-grey-100 text-grey-700 border border-grey-200' },
  expired:          { label: 'Hidden',  cls: 'bg-grey-100 text-grey-600 border border-grey-200' },
};

interface DraftCardProps {
  draft: DraftSummary;
  showStatusBadge: boolean;
  onOpen: () => void;
}

function DraftCard({ draft, showStatusBadge, onOpen }: DraftCardProps) {
  // Cap projection — only meaningful for pending drafts. Hide for terminal
  // statuses (Sent / Cancelled). Mirrors notification-sender.applyCapChecks.
  const inTestMode = draft.target_test_mode_enabled === true;
  const isPending = draft.status === 'pending_approval' || draft.status === 'approved';
  const recipientCapHits = (!isPending || inTestMode) ? 0 : (draft.recipient_cap_hit_count ?? 0);
  const survivingIncluded = Math.max(0, draft.included_count - recipientCapHits);
  const siteBudget =
    draft.per_publication_cap !== null && draft.per_publication_cap !== undefined
      ? Math.max(0, draft.per_publication_cap - draft.sent_today_target)
      : Infinity;
  const siteCapHits = (!isPending || inTestMode) ? 0 : Math.max(0, survivingIncluded - siteBudget);
  const totalCapHits = recipientCapHits + siteCapHits;
  const willSendCount = isPending ? Math.max(0, survivingIncluded - siteCapHits) : 0;

  const statusMeta = STATUS_BADGE[draft.status] ?? STATUS_BADGE.pending_approval;

  return (
    <button
      onClick={onOpen}
      className="w-full text-left rounded-lg border border-grey-200 bg-surface p-3 hover:border-primary-300 hover:bg-primary-50/30 transition-colors"
    >
      {/* Top line: pub · ticker · timestamp (+ status badge in non-default views) */}
      <div className="flex items-center gap-2 text-xs text-grey-600 mb-1">
        {showStatusBadge && (
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide ${statusMeta.cls}`}>
            {statusMeta.label}
          </span>
        )}
        <Mail className="h-3.5 w-3.5 text-grey-400" />
        <span className="font-medium text-grey-700">{draft.target_name}</span>
        {draft.entity_identifier && (
          <>
            <span className="text-grey-400">·</span>
            <span className="font-mono text-[11px] text-grey-700">{draft.entity_identifier}</span>
          </>
        )}
        <CoverageBadges tiers={draft.coverage_tiers} />
        {inTestMode && isPending && (
          <span className="text-warning-700 font-medium">· TEST</span>
        )}
        <PresencePill resourceType="notification_draft" resourceId={draft.draft_id} compact />
        <span className="ml-auto tabular-nums text-grey-500">{formatTimestamp(draft.created_at)}</span>
      </div>

      {/* Title (article) */}
      <div className="text-sm font-medium text-grey-900 truncate">
        {draft.article_title || draft.rendered_subject}
      </div>

      {/* Bottom line: send projection + cap badges (pending only) */}
      <div className="mt-1 flex items-center gap-3 text-xs text-grey-500 flex-wrap">
        {isPending && (
          <>
            <span className="text-success-700 font-medium tabular-nums">
              {willSendCount} will send
            </span>
            {totalCapHits > 0 && (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-danger-50 text-danger-700 px-2 py-0.5 tabular-nums font-medium"
                title={[
                  recipientCapHits > 0 && `${recipientCapHits} at recipient cap`,
                  siteCapHits > 0 && `${siteCapHits} over site budget`,
                ].filter(Boolean).join(' · ')}
              >
                <AlertTriangle className="h-3 w-3" />
                {totalCapHits} capped
              </span>
            )}
            {draft.excluded_count > 0 && (
              <span className="tabular-nums">
                {draft.excluded_count} excluded
              </span>
            )}
            <span className="ml-auto inline-flex items-center gap-1 text-grey-400">
              <ArrowRight className="h-3.5 w-3.5" />
            </span>
          </>
        )}
        {!isPending && (
          <>
            <span className="tabular-nums">
              {draft.included_count} included · {draft.excluded_count} excluded
            </span>
            <span className="ml-auto inline-flex items-center gap-1 text-grey-400">
              <ArrowRight className="h-3.5 w-3.5" />
            </span>
          </>
        )}
      </div>
    </button>
  );
}

/** Date / time helpers. Match Social's formatting so day tabs and card
 *  timestamps look consistent across the manager. */
function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = (d.getMonth() + 1).toString().padStart(2, '0');
  const day = d.getDate().toString().padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function todayYMD(): string {
  return ymd(new Date());
}

function shiftDays(date: string, deltaDays: number): string {
  const d = new Date(date + 'T00:00:00');
  d.setDate(d.getDate() + deltaDays);
  return ymd(d);
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  // dd/mm/yyyy, hh:mm:ss — matches the Social card format.
  const date = d.toLocaleDateString(undefined, {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
  const time = d.toLocaleTimeString(undefined, {
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
  return `${date}, ${time}`;
}

/** Status/trust/cap badge stack for a draft recipient — shared between the
 *  md+ recipients table and the phone card list. The `title=` hints remain
 *  for desktop; the badge text itself is the touch-readable signal. */
function RecipientStatusStack({ r, isOperatorAdded, wasIncluded, wasExcluded, hardLocked, operatorOverriding, recipientCapHit, siteCapHit, editError }: {
  r: any;
  isOperatorAdded: boolean;
  wasIncluded: boolean;
  wasExcluded: boolean;
  hardLocked: boolean;
  operatorOverriding: boolean;
  recipientCapHit: boolean;
  siteCapHit: boolean;
  editError: string | null;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      {isOperatorAdded && (
        <span className="inline-flex items-center gap-1 text-primary-700 font-medium" title="Added by operator via the notification draft UI">
          <UserPlus className="h-3 w-3" /> {r.kind === 'entity_channel' ? 'operator-added (channel)' : 'operator-added'}
        </span>
      )}
      {/* Trust-ledger badge — recipient_review state for (email, entity). */}
      {r.review_status === 'approved' && (
        <span className="inline-flex items-center gap-1 text-success-700 font-medium" title="This (recipient, entity) pair is trusted in the ledger. Send can fire without re-approval.">
          <CheckCircle2 className="h-3 w-3" /> trusted
        </span>
      )}
      {r.review_status === 'pending' && (
        <span className="inline-flex items-center gap-1 text-grey-600" title="Pair enqueued for first-touch review. Approve via this drawer or /notifications/recipient-reviews.">
          pending review
        </span>
      )}
      {r.review_status === 'rejected' && (
        <span className="inline-flex items-center gap-1 text-danger-700" title="Pair explicitly rejected. Soft-lock — re-enable via /notifications/recipient-reviews.">
          ledger: rejected
        </span>
      )}
      {r.review_status === null && (
        <span className="inline-flex items-center gap-1 text-grey-500 italic" title="Pair never seen — first time. Approve & send to trust going forward.">
          new pair
        </span>
      )}
      {wasIncluded && !operatorOverriding && (
        <span className="inline-flex items-center gap-1 text-success-700">
          <CheckCircle2 className="h-3.5 w-3.5" /> included
        </span>
      )}
      {wasExcluded && hardLocked && (
        <span className="inline-flex items-center gap-1 text-danger-700">
          <ShieldAlert className="h-3.5 w-3.5" /> {explainReason(r.reason)}
        </span>
      )}
      {wasExcluded && !hardLocked && !operatorOverriding && (
        <span className="text-grey-600">{explainReason(r.reason)}</span>
      )}
      {operatorOverriding && (
        <span className="inline-flex items-center gap-1 text-warning-700 font-medium">
          override: was {explainReason(r.reason)}
        </span>
      )}
      {recipientCapHit && (
        <span className="inline-flex items-center gap-1 text-danger-700 text-xs font-medium" title="This recipient already received the maximum number of sends from this publication today. Approving will drop them at send time.">
          <AlertTriangle className="h-3 w-3" /> recipient cap reached
        </span>
      )}
      {siteCapHit && (
        <span className="inline-flex items-center gap-1 text-danger-700 text-xs font-medium" title="This publication's daily send budget will be exhausted before this row. Approving will drop them at send time.">
          <AlertTriangle className="h-3 w-3" /> site cap reached
        </span>
      )}
      {editError && (
        <span className="text-error-700 text-xs">{editError}</span>
      )}
    </div>
  );
}

function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

// ---------------------------------------------------------------------
// Drawer (full draft view + action bar)
// ---------------------------------------------------------------------

function DraftDrawer({ draftId, onClose }: { draftId: string; onClose: () => void }) {
  const qc = useQueryClient();

  // Soft-presence beacon: announce that this operator has the drawer open
  // so other operators see a "kate reviewing" chip on their copy of this
  // draft's card. Auto-closes on unmount + beforeunload (see usePresence).
  usePresence({ resourceType: 'notification_draft', resourceId: draftId });

  const { data: draft, isLoading } = useQuery({
    queryKey: ['notifications', 'draft', draftId],
    queryFn: () => getDraft(draftId),
  });

  // Pull live target settings so we know whether test_mode is on at THIS moment
  // (the draft's target_config snapshot is fine for almost everything, but
  // test_mode is a flag the operator may have flipped since draft creation).
  const { data: target } = useQuery({
    enabled: !!draft,
    queryKey: ['notifications', 'target-settings', draft?.target_id],
    queryFn: () => getTargetNotificationSettings(draft!.target_id),
  });

  // Per-recipient toggle state — initialised from the draft's verdicts but
  // operator can toggle. Map keyed on lowercased email.
  const [toggles, setToggles] = useState<Record<string, { included: boolean; operator_override: boolean }>>({});
  const [subjectOverride, setSubjectOverride] = useState<string>('');
  // Persisted body editor — seeded from draft.operator_body. Save re-renders
  // the compliance shell server-side (footer/opt-out stay locked).
  const [bodyDraft, setBodyDraft] = useState<string>('');
  // The subject/body the editor ORIGINALLY loaded — the content-snapshot base
  // for the optimistic lock. Captured once at seed time and NOT refreshed by
  // WebSocket refetches, so the save can detect a genuine concurrent content
  // change without false-conflicting on background draft refreshes.
  const [loadedSubject, setLoadedSubject] = useState<string>('');
  const [loadedBody, setLoadedBody] = useState<string>('');
  const [previewMode, setPreviewMode] = useState<PreviewMode>('full');
  const [notes, setNotes] = useState<string>('');

  // 2026-06-05 enrichment: per-row inline edit + add modal.
  const [editingRecipientId, setEditingRecipientId] = useState<string | null>(null);
  const [editEmail, setEditEmail] = useState('');
  const [editName, setEditName] = useState('');
  const [editError, setEditError] = useState<string | null>(null);
  const [editPending, setEditPending] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);

  const patchMutation = useMutation({
    mutationFn: ({ recipientId, body }: { recipientId: string; body: { email?: string; name?: string } }) =>
      patchRecipient(draft!.draft_id, recipientId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['draft', draft!.draft_id] });
      qc.invalidateQueries({ queryKey: ['queue'] });
      setEditingRecipientId(null);
      setEditError(null);
    },
    onError: (e: any) => setEditError(e?.response?.data?.detail || e?.message || 'Patch failed.'),
    onSettled: () => setEditPending(false),
  });

  const deleteMutation = useMutation({
    mutationFn: (recipientId: string) => deleteRecipient(draft!.draft_id, recipientId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['draft', draft!.draft_id] });
      qc.invalidateQueries({ queryKey: ['queue'] });
    },
  });

  // Persisted content edit (subject + body), optimistic-locked on updated_at.
  // A 409 (another operator edited it first) is routed through the shared
  // handleMutationConflict — the editor stays open with the operator's text.
  const saveContentMutation = useMutation({
    mutationFn: () =>
      updateDraftContent(
        draft!.draft_id,
        { subject: subjectOverride, body: bodyDraft },
        { subject: loadedSubject, body: loadedBody },
      ),
    onSuccess: (res) => {
      // Reseed the editor + the content-snapshot base from the persisted values,
      // and refresh preview/list. The new base means a subsequent edit locks
      // against what we just saved.
      setSubjectOverride(res.rendered_subject);
      setBodyDraft(res.operator_body);
      setLoadedSubject(res.rendered_subject);
      setLoadedBody(res.operator_body);
      qc.invalidateQueries({ queryKey: ['notifications', 'draft', draft!.draft_id] });
      qc.invalidateQueries({ queryKey: ['notifications', 'queue'] });
      qc.invalidateQueries({ queryKey: ['notifications', 'queue-grouped'] });
    },
    onError: (e: any) => {
      if (handleMutationConflict(e, qc, [['notifications', 'draft', draft!.draft_id]])) return;
      window.alert('Failed to save: ' + (e?.response?.data?.detail?.message || e?.message || 'Unknown error'));
    },
  });

  const startEdit = (r: DraftRecipient) => {
    setEditingRecipientId(r.recipient_id || r.email);
    setEditEmail(r.email);
    setEditName(r.name || '');
    setEditError(null);
  };
  const cancelEdit = () => {
    setEditingRecipientId(null);
    setEditError(null);
  };
  const saveEdit = (r: DraftRecipient) => {
    if (!r.recipient_id) {
      setEditError(
        'This recipient is from a legacy draft and lacks a stable id. Re-open the drawer to backfill, then retry.',
      );
      return;
    }
    const body: { email?: string; name?: string } = {};
    if (editEmail.trim() && editEmail.trim().toLowerCase() !== r.email) {
      body.email = editEmail.trim().toLowerCase();
    }
    if (editName.trim() !== (r.name || '')) {
      body.name = editName.trim();
    }
    if (Object.keys(body).length === 0) {
      cancelEdit();
      return;
    }
    setEditPending(true);
    patchMutation.mutate({ recipientId: r.recipient_id, body });
  };

  // Seed toggles when draft loads.
  useEffect(() => {
    if (!draft) return;
    const seed: Record<string, { included: boolean; operator_override: boolean }> = {};
    for (const r of draft.recipients) {
      const email = r.email.toLowerCase();
      seed[email] = {
        included: r.verdict === 'included',
        operator_override: false,
      };
    }
    setToggles(seed);
    setSubjectOverride(draft.rendered_subject);
    setBodyDraft(draft.operator_body ?? '');
    // Capture the content-snapshot base for the optimistic lock — only when this
    // is a different draft (NOT on every refetch), so a WebSocket-driven refresh
    // can't quietly advance the base and defeat the lock mid-edit.
    setLoadedSubject(draft.rendered_subject);
    setLoadedBody(draft.operator_body ?? '');
  }, [draft?.draft_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const testModeActive = target?.config?.test_mode_enabled === true;
  const testRecipient = target?.config?.test_mode_recipient || '';

  // Compute per-recipient + per-site cap projections.
  //
  // Cap-projection rules (mirror notification-sender.applyCapChecks):
  //   - Test mode bypasses both caps. Badge nothing.
  //   - Real mode:
  //     • Per-recipient: badge if `recipient_counts[email] >= per_recipient_cap`
  //       (already at quota — next send to this address would be capped)
  //     • Per-site: walk the to-be-sent list in display order, drawing
  //       down `per_publication_cap - sent_today_target` budget. Rows
  //       beyond the budget get the site-cap badge.
  //   - Operator override doesn't bypass caps (they fire at send time
  //     regardless of override); a row can carry BOTH the override
  //     marker AND a cap badge.
  const capProjection = useMemo(() => {
    if (!draft) return { recipientCap: new Set<string>(), siteCap: new Set<string>() };
    const recipientCap = new Set<string>();
    const siteCap = new Set<string>();
    if (testModeActive) return { recipientCap, siteCap };

    const preview = draft.cap_preview;
    const perRecCap = preview?.per_recipient_cap ?? 1;
    const perPubCap = preview?.per_publication_cap;
    const sentTodayTarget = preview?.sent_today_target ?? 0;
    const recipientCounts = preview?.recipient_counts ?? {};

    let remainingBudget = perPubCap !== null && perPubCap !== undefined
      ? Math.max(0, perPubCap - sentTodayTarget)
      : Infinity;

    for (const r of draft.recipients) {
      const key = r.email.toLowerCase();
      const t = toggles[key];
      if (!t?.included) continue;
      if (isHardLocked(r.reason)) continue;

      const alreadySent = recipientCounts[key] ?? 0;
      if (perRecCap >= 0 && alreadySent >= perRecCap) {
        recipientCap.add(key);
        continue; // recipient-cap-hit rows don't consume site budget
      }
      if (remainingBudget <= 0) {
        siteCap.add(key);
      } else {
        remainingBudget -= 1;
      }
    }
    return { recipientCap, siteCap };
  }, [draft, toggles, testModeActive]);

  // Compute current selection summary.
  const counts = useMemo(() => {
    if (!draft) return { willSend: 0, dropped: 0, override: 0, hardLocked: 0, recipientCapHits: 0, siteCapHits: 0 };
    let willSend = 0, dropped = 0, override = 0, hardLocked = 0;
    for (const r of draft.recipients) {
      const t = toggles[r.email.toLowerCase()];
      if (!t) continue;
      if (isHardLocked(r.reason)) { hardLocked++; continue; }
      if (t.included) {
        willSend++;
        if (r.verdict === 'excluded' && t.operator_override) override++;
      } else {
        dropped++;
      }
    }
    return {
      willSend,
      dropped,
      override,
      hardLocked,
      recipientCapHits: capProjection.recipientCap.size,
      siteCapHits: capProjection.siteCap.size,
    };
  }, [draft, toggles, capProjection]);

  // Build the recipients_final payload for /approve-and-send and /send (same shape).
  const buildRecipientsFinal = (): FinalRecipient[] =>
    draft!.recipients.map(r => {
      const t = toggles[r.email.toLowerCase()];
      return {
        email: r.email,
        included: t?.included ?? false,
        operator_override: !!t?.operator_override,
      };
    });

  // List of currently-checked emails — used both for "approve recipients only"
  // payload and to compute whether the "Send" button is enabled (all approved).
  const checkedEmails: string[] = useMemo(() => {
    if (!draft) return [];
    return draft.recipients
      .filter(r => toggles[r.email.toLowerCase()]?.included)
      .map(r => r.email);
  }, [draft, toggles]);

  // Send button enabled only when every checked recipient is already
  // 'approved' in the ledger. "Approve & send" works regardless.
  const allCheckedApproved = useMemo(() => {
    if (!draft || checkedEmails.length === 0) return false;
    return draft.recipients.every(r => {
      const checked = toggles[r.email.toLowerCase()]?.included;
      if (!checked) return true;
      return r.review_status === 'approved';
    });
  }, [draft, toggles, checkedEmails]);

  // "Approve recipients only" — flips ledger to 'approved' for every
  // checked row but does NOT fire the email. Draft stays in queue.
  const approveRecipientsMutation = useMutation({
    mutationFn: () => approveRecipients(draftId, checkedEmails),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications', 'queue'] });
      qc.invalidateQueries({ queryKey: ['notifications', 'draft', draftId] });
      // Don't close — operator may want to continue editing/sending.
    },
  });

  // Multi-operator concurrency: if another operator already approved the
  // same draft between our last refetch and our click, the server returns
  // 409 + the winner's identity. handleMutationConflict surfaces the
  // message, invalidates queries so the drawer reflects the winner's
  // state, and returns true to suppress further error UX.
  const onSendConflict = (e: any) => handleMutationConflict(e, qc, [
    ['notifications', 'queue'],
    ['notifications', 'draft', draftId],
  ]);

  const approveAndSendMutation = useMutation({
    mutationFn: () => approveAndSendDraft(draftId, {
      recipients_final: buildRecipientsFinal(),
      subject_override: subjectOverride !== draft!.rendered_subject ? subjectOverride : undefined,
      notes: notes || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications', 'queue'] });
      onClose();
    },
    onError: (e: any) => {
      if (onSendConflict(e)) onClose();
    },
  });

  const sendMutation = useMutation({
    mutationFn: () => sendDraft(draftId, {
      recipients_final: buildRecipientsFinal(),
      subject_override: subjectOverride !== draft!.rendered_subject ? subjectOverride : undefined,
      notes: notes || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications', 'queue'] });
      onClose();
    },
    onError: (e: any) => {
      if (onSendConflict(e)) onClose();
    },
  });

  // Schedule: same recipient/edits snapshot as approve-and-send, but defers
  // the actual Mailjet send to a pipeline_jobs row that fires at scheduled_for.
  // Closes the drawer functionality gap where operator-override recipients
  // (e.g. trust-ledger-approved but originally excluded) had no schedule path.
  const scheduleMutation = useMutation({
    mutationFn: (scheduledForUtcIso: string) =>
      scheduleDraft(draftId, {
        recipients_final: buildRecipientsFinal(),
        scheduled_for: scheduledForUtcIso,
        notes: notes || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications', 'queue'] });
      qc.invalidateQueries({ queryKey: ['notifications', 'queue-grouped'] });
      qc.invalidateQueries({ queryKey: ['notifications-scheduled'] });
      onClose();
    },
  });
  const [schedulePickerOpen, setSchedulePickerOpen] = useState(false);

  const rejectMutation = useMutation({
    mutationFn: () => rejectDraft(draftId, notes || undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications', 'queue'] });
      onClose();
    },
  });

  // NB: cancelMutation removed — the bottom-bar "Cancel" button now just
  // closes the drawer without changing draft state, matching standard
  // dialog semantics. Reject remains as the deliberate decline action.
  // Server-side cancelDraft endpoint is still available if we add an
  // explicit "Discard" button later.

  const toggleRecipient = (email: string, isCurrentlyExcluded: boolean) => {
    setToggles(prev => {
      const next = { ...prev };
      const key = email.toLowerCase();
      const curr = next[key] ?? { included: false, operator_override: false };
      next[key] = {
        included: !curr.included,
        // Setting operator_override only when re-including an originally-excluded row.
        operator_override: !curr.included && isCurrentlyExcluded,
      };
      return next;
    });
  };

  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex" onClick={onClose}>
      <div
        className="ml-auto w-full max-w-5xl bg-surface shadow-xl h-full overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-surface border-b border-grey-200 px-3 sm:px-6 py-4 z-10 flex items-start justify-between gap-2">
          <div>
            <div className="text-xs text-grey-500 uppercase tracking-wide">Draft</div>
            <div className="text-lg font-semibold text-grey-900">
              {draft?.article_title || 'Loading…'}
            </div>
            <div className="text-sm text-grey-600 mt-0.5">
              {draft?.target_name} · {draft?.notification_from_email}
            </div>
          </div>
          <button onClick={onClose} className="p-2 -m-1 rounded hover:bg-grey-100 active:bg-grey-200 touch-target">
            <X className="h-5 w-5 text-grey-500" />
          </button>
        </div>

        {isLoading && <div className="p-6 text-sm text-grey-500">Loading draft…</div>}

        {draft && (
          <>
            {testModeActive && (
              <div className="mx-3 sm:mx-6 mt-4 rounded-lg border border-warning-200 bg-warning-50 p-3 text-sm text-warning-800 flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Test mode active on {draft.target_name}.</strong> If you approve, the {counts.willSend} planned recipients will be replaced with <code className="px-1 py-0.5 bg-warning-100 rounded font-mono text-xs">{testRecipient}</code>. The journalists will NOT be contacted. Mailjet contact-list operations are skipped.
                </div>
              </div>
            )}

            {/* Recipient table */}
            <section className="px-3 sm:px-6 py-4 border-b border-grey-200">
              <h3 className="text-sm font-semibold text-grey-900 mb-3 flex items-center gap-2 flex-wrap">
                Recipients
                <button
                  type="button"
                  onClick={() => setAddModalOpen(true)}
                  className="inline-flex items-center gap-1 text-xs font-normal text-primary-700 hover:text-primary-900 border border-primary-200 rounded px-2 py-0.5 hover:bg-primary-50"
                  title="Add a person contact or entity-level channel to this draft"
                >
                  <Plus className="w-3 h-3" /> Add recipient
                </button>
                <span className="text-xs font-normal text-grey-500">
                  · {counts.willSend - counts.recipientCapHits - counts.siteCapHits} will send
                  {counts.override > 0 && (
                    <span className="text-warning-700"> ({counts.override} operator override)</span>
                  )}
                  {counts.dropped > 0 && <span> · {counts.dropped} dropped</span>}
                  {counts.recipientCapHits > 0 && (
                    <span className="text-danger-700"> · {counts.recipientCapHits} will hit recipient cap</span>
                  )}
                  {counts.siteCapHits > 0 && (
                    <span className="text-danger-700"> · {counts.siteCapHits} will hit site cap</span>
                  )}
                </span>
                {draft.cap_preview?.per_publication_cap !== null && draft.cap_preview?.per_publication_cap !== undefined && !testModeActive && (
                  <span className="text-xs text-grey-500 ml-auto font-normal">
                    Site budget: {Math.max(0, (draft.cap_preview.per_publication_cap ?? 0) - draft.cap_preview.sent_today_target)} / {draft.cap_preview.per_publication_cap} remaining today
                  </span>
                )}
              </h3>
              <TableScroll className="hidden md:block rounded-lg border border-grey-200">
                <table className="w-full text-sm">
                  <thead className="bg-grey-50 text-xs text-grey-600 uppercase">
                    <tr>
                      <th className="px-3 py-2 text-left w-10"></th>
                      <th className="px-3 py-2 text-left">Email</th>
                      <th className="px-3 py-2 text-left">Name</th>
                      <th className="px-3 py-2 text-left">Role</th>
                      <th className="px-3 py-2 text-right">Confidence</th>
                      <th className="px-3 py-2 text-left">Organisation</th>
                      <th className="px-3 py-2 text-left">Status</th>
                      <th className="px-3 py-2 text-right w-16"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {draft.recipients.map(r => {
                      const key = r.email.toLowerCase();
                      const rowId = r.recipient_id || r.email;
                      const t = toggles[key];
                      const checked = t?.included ?? false;
                      const hardLocked = isHardLocked(r.reason);
                      const wasIncluded = r.verdict === 'included';
                      const wasExcluded = r.verdict === 'excluded';
                      const operatorOverriding = wasExcluded && checked && isSoftLocked(r.reason);
                      const isEditing = editingRecipientId === rowId;
                      const isOperatorAdded = r.source === 'operator_added';
                      return (
                        <tr key={rowId} className={`border-t border-grey-100 ${operatorOverriding ? 'bg-warning-50/50' : ''} ${isOperatorAdded ? 'bg-primary-50/30' : ''}`}>
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={hardLocked}
                              onChange={() => toggleRecipient(r.email, wasExcluded)}
                              title={hardLocked ? `Cannot include — hard-locked (${explainReason(r.reason)})` : ''}
                              className="cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                            />
                          </td>
                          <td className="px-3 py-2 font-mono text-xs text-grey-900">
                            {isEditing ? (
                              <input
                                type="email"
                                value={editEmail}
                                onChange={(e) => setEditEmail(e.target.value)}
                                className="w-full border border-grey-300 rounded px-1.5 py-0.5 font-mono text-xs"
                                autoFocus
                              />
                            ) : (
                              r.email || <span className="italic text-grey-400">no email</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-grey-700">
                            {isEditing ? (
                              <input
                                type="text"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                className="w-full border border-grey-300 rounded px-1.5 py-0.5 text-sm"
                              />
                            ) : (
                              r.name || '—'
                            )}
                          </td>
                          <td className="px-3 py-2 text-grey-700">{r.role}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-grey-700">
                            {r.confidence.toFixed(2)}
                          </td>
                          <td className="px-3 py-2 text-grey-700">
                            <div className="flex items-center gap-1">
                              <span className="truncate max-w-[180px]" title={r.organisation || ''}>
                                {r.organisation || '—'}
                              </span>
                              {r.organisation_anomaly && (
                                <span title="Organisation doesn't match entity — operator review">
                                  <AlertTriangle className="h-3.5 w-3.5 text-warning-600 flex-shrink-0" />
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-xs">
                            <RecipientStatusStack
                              r={r}
                              isOperatorAdded={isOperatorAdded}
                              wasIncluded={wasIncluded}
                              wasExcluded={wasExcluded}
                              hardLocked={hardLocked}
                              operatorOverriding={operatorOverriding}
                              recipientCapHit={capProjection.recipientCap.has(key)}
                              siteCapHit={capProjection.siteCap.has(key)}
                              editError={isEditing ? editError : null}
                            />
                          </td>
                          <td className="px-2 py-2 text-right whitespace-nowrap">
                            {isEditing ? (
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  type="button"
                                  onClick={() => saveEdit(r)}
                                  disabled={editPending}
                                  className="text-xs px-2 py-0.5 rounded bg-primary-600 text-white hover:bg-primary-700 disabled:bg-primary-300"
                                >
                                  {editPending ? '…' : 'Save'}
                                </button>
                                <button
                                  type="button"
                                  onClick={cancelEdit}
                                  className="text-xs px-2 py-0.5 rounded border border-grey-300 hover:bg-grey-50"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center justify-end gap-1 text-grey-500">
                                <button
                                  type="button"
                                  onClick={() => startEdit(r)}
                                  className="p-1 rounded hover:bg-grey-100 hover:text-grey-800"
                                  title="Edit email / name"
                                >
                                  <Edit2 className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (!r.recipient_id) return;
                                    if (confirm(`Remove ${r.email || r.name || 'this recipient'}?`)) {
                                      deleteMutation.mutate(r.recipient_id);
                                    }
                                  }}
                                  disabled={!r.recipient_id}
                                  className="p-1 rounded hover:bg-error-50 hover:text-error-700 disabled:opacity-30 disabled:cursor-not-allowed"
                                  title={r.recipient_id ? 'Remove recipient' : 'Legacy row — re-open drawer to enable removal'}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </TableScroll>
              {/* Phones: card per recipient — same computed state + actions */}
              <div className="md:hidden rounded-lg border border-grey-200 divide-y divide-grey-100">
                {draft.recipients.map(r => {
                  const key = r.email.toLowerCase();
                  const rowId = r.recipient_id || r.email;
                  const t = toggles[key];
                  const checked = t?.included ?? false;
                  const hardLocked = isHardLocked(r.reason);
                  const wasIncluded = r.verdict === 'included';
                  const wasExcluded = r.verdict === 'excluded';
                  const operatorOverriding = wasExcluded && checked && isSoftLocked(r.reason);
                  const isEditing = editingRecipientId === rowId;
                  const isOperatorAdded = r.source === 'operator_added';
                  return (
                    <div key={rowId} className={`p-3 ${operatorOverriding ? 'bg-warning-50/50' : ''} ${isOperatorAdded ? 'bg-primary-50/30' : ''}`}>
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={hardLocked}
                          onChange={() => toggleRecipient(r.email, wasExcluded)}
                          className="h-4 w-4 mt-0.5 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                        />
                        <div className="flex-1 min-w-0">
                          {isEditing ? (
                            <div className="space-y-1.5">
                              <input
                                type="email"
                                value={editEmail}
                                onChange={(e) => setEditEmail(e.target.value)}
                                className="w-full border border-grey-300 rounded px-2 py-1.5 font-mono text-xs"
                                autoFocus
                              />
                              <input
                                type="text"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                placeholder="Name"
                                className="w-full border border-grey-300 rounded px-2 py-1.5 text-sm"
                              />
                            </div>
                          ) : (
                            <>
                              <div className="text-sm text-grey-900 font-medium">{r.name || '—'}</div>
                              <div className="font-mono text-xs text-grey-700 break-all">
                                {r.email || <span className="italic text-grey-400">no email</span>}
                              </div>
                            </>
                          )}
                          <div className="text-xs text-grey-600 mt-0.5">
                            {r.role} · confidence {r.confidence.toFixed(2)}
                            {r.organisation && <> · {r.organisation}</>}
                            {r.organisation_anomaly && (
                              <span className="ml-1 text-warning-700 font-medium">org ≠ entity</span>
                            )}
                          </div>
                          <div className="mt-1.5 text-xs">
                            <RecipientStatusStack
                              r={r}
                              isOperatorAdded={isOperatorAdded}
                              wasIncluded={wasIncluded}
                              wasExcluded={wasExcluded}
                              hardLocked={hardLocked}
                              operatorOverriding={operatorOverriding}
                              recipientCapHit={capProjection.recipientCap.has(key)}
                              siteCapHit={capProjection.siteCap.has(key)}
                              editError={isEditing ? editError : null}
                            />
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            {isEditing ? (
                              <>
                                <button
                                  type="button"
                                  onClick={() => saveEdit(r)}
                                  disabled={editPending}
                                  className="text-xs px-3 py-2 rounded bg-primary-600 text-white hover:bg-primary-700 disabled:bg-primary-300"
                                >
                                  {editPending ? '…' : 'Save'}
                                </button>
                                <button
                                  type="button"
                                  onClick={cancelEdit}
                                  className="text-xs px-3 py-2 rounded border border-grey-300 hover:bg-grey-50"
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  onClick={() => startEdit(r)}
                                  className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded border border-grey-300 text-grey-700 hover:bg-grey-50"
                                >
                                  <Edit2 className="h-3.5 w-3.5" /> Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (!r.recipient_id) return;
                                    if (confirm(`Remove ${r.email || r.name || 'this recipient'}?`)) {
                                      deleteMutation.mutate(r.recipient_id);
                                    }
                                  }}
                                  disabled={!r.recipient_id}
                                  className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded border border-grey-300 text-grey-700 hover:bg-error-50 hover:text-error-700 disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                  <Trash2 className="h-3.5 w-3.5" /> Remove
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Subject + email preview */}
            <section className="px-3 sm:px-6 py-4 border-b border-grey-200">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-grey-900">Email preview</h3>
                <div className="text-xs text-grey-500">
                  From: <span className="font-mono">{draft.notification_from_email}</span>
                  &nbsp;· Reply-To: <span className="font-mono">{draft.notification_from_email}</span>
                  &nbsp;· BCC: <span className="font-mono">{draft.bcc_email}</span>
                </div>
              </div>
              {/* Persisted content editor — subject + body. The legal footer
                  and unsubscribe link are re-applied by the template on save,
                  so they're never editable here. Optimistic-locked: a Save that
                  races another operator returns a 409 (handled). */}
              {(() => {
                const contentDirty =
                  subjectOverride !== draft.rendered_subject ||
                  bodyDraft !== (draft.operator_body ?? '');
                const contentEditable =
                  draft.status === 'pending_approval' ||
                  draft.status === 'approved' ||
                  draft.status === 'scheduled';
                return (
                  <div className="mb-3 space-y-2">
                    <div>
                      <label className="block text-xs font-medium text-grey-600 mb-1">Subject</label>
                      <input
                        type="text"
                        value={subjectOverride}
                        onChange={e => setSubjectOverride(e.target.value)}
                        disabled={!contentEditable}
                        className="w-full px-3 py-1.5 text-sm border border-grey-300 rounded font-medium disabled:bg-grey-50 disabled:text-grey-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-grey-600 mb-1">
                        Body
                        <span className="ml-1 font-normal text-grey-400">
                          — plain text; the legal footer &amp; unsubscribe link are added automatically
                        </span>
                      </label>
                      <textarea
                        value={bodyDraft}
                        onChange={e => setBodyDraft(e.target.value)}
                        disabled={!contentEditable}
                        rows={6}
                        placeholder="Leave blank to use the standard corrections-invited copy. Type here to replace the body paragraphs — separate paragraphs with a blank line."
                        className="w-full px-3 py-2 text-sm border border-grey-300 rounded leading-relaxed disabled:bg-grey-50 disabled:text-grey-500"
                      />
                    </div>
                    {contentEditable && (
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => saveContentMutation.mutate()}
                          disabled={!contentDirty || saveContentMutation.isPending}
                          className="px-3 py-1.5 text-xs font-semibold rounded bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {saveContentMutation.isPending ? 'Saving…' : 'Save changes'}
                        </button>
                        {contentDirty && !saveContentMutation.isPending && (
                          <span className="text-xs text-warning-700">
                            Unsaved edits — Save to update the preview &amp; what gets sent.
                          </span>
                        )}
                        {!contentDirty && draft.edited_by && (
                          <span className="text-xs text-grey-400">
                            Last edited by {draft.edited_by.split('@')[0]}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}

              <div className="flex gap-1 border-b border-grey-200 mb-3">
                {PREVIEW_TABS.map(tab => {
                  const Icon = tab.icon;
                  const active = tab.id === previewMode;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setPreviewMode(tab.id)}
                      className={`px-3 py-1.5 text-xs font-medium border-b-2 -mb-px transition-colors inline-flex items-center gap-1.5 ${
                        active
                          ? 'border-primary-600 text-primary-700'
                          : 'border-transparent text-grey-600 hover:text-grey-900'
                      }`}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      {tab.label}
                    </button>
                  );
                })}
              </div>

              <div className="rounded-lg border border-grey-200 bg-grey-50 p-4 min-h-[300px] max-h-[500px] overflow-auto">
                <PreviewBody
                  mode={previewMode}
                  html={draft.rendered_html}
                  text={draft.rendered_text}
                />
              </div>
            </section>

            {/* Notes + action bar */}
            <section className="px-3 sm:px-6 py-4 border-b border-grey-200">
              <label className="block text-xs font-medium text-grey-600 mb-1">
                Notes (optional, recorded on the audit row)
              </label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={2}
                className="w-full px-3 py-1.5 text-sm border border-grey-300 rounded resize-none"
                placeholder="e.g. flagged the alt-PR contact at competitor agency"
              />
            </section>

            <div className="sticky bottom-0 bg-surface border-t border-grey-200 px-3 sm:px-6 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex items-center justify-between gap-x-4 gap-y-2 flex-wrap">
              <div className="text-xs text-grey-600">
                {counts.willSend === 0 ? (
                  <span className="text-grey-500">No recipients selected — cannot approve.</span>
                ) : (
                  <>
                    Will {testModeActive ? 'test-send' : 'send'} to <strong>{counts.willSend - counts.recipientCapHits - counts.siteCapHits}</strong> recipient{counts.willSend - counts.recipientCapHits - counts.siteCapHits === 1 ? '' : 's'}
                    {counts.override > 0 && (
                      <span className="ml-2 text-warning-700">({counts.override} operator override)</span>
                    )}
                    {(counts.recipientCapHits + counts.siteCapHits) > 0 && (
                      <span className="ml-2 text-danger-700">
                        — {counts.recipientCapHits + counts.siteCapHits} will be dropped by cap at send time
                      </span>
                    )}
                  </>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={onClose}
                  className="px-3 py-1.5 text-sm border border-grey-300 rounded text-grey-700 hover:bg-grey-50"
                  title="Close the drawer without changing the draft. The draft stays in the queue."
                >
                  Cancel
                </button>
                <button
                  onClick={() => rejectMutation.mutate()}
                  disabled={rejectMutation.isPending}
                  className="px-3 py-1.5 text-sm border border-danger-300 rounded text-danger-700 hover:bg-danger-50 disabled:opacity-50"
                  title="Mark this draft as rejected. No email sent."
                >
                  Reject
                </button>
                <button
                  onClick={() => approveRecipientsMutation.mutate()}
                  disabled={checkedEmails.length === 0 || approveRecipientsMutation.isPending}
                  className="px-3 py-1.5 text-sm border border-primary-300 rounded text-primary-700 hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Mark the checked recipients as trusted for future drafts. No email sent. Draft stays in queue."
                >
                  {approveRecipientsMutation.isPending ? 'Approving…' : `Approve recipients (${checkedEmails.length})`}
                </button>
                <button
                  onClick={() => sendMutation.mutate()}
                  disabled={!allCheckedApproved || checkedEmails.length === 0 || sendMutation.isPending}
                  className={`px-3 py-1.5 text-sm border rounded inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                    testModeActive
                      ? 'border-warning-300 text-warning-800 hover:bg-warning-50'
                      : 'border-primary-300 text-primary-700 hover:bg-primary-50'
                  }`}
                  title={
                    !allCheckedApproved
                      ? 'All checked recipients must be approved first — use Approve & Send, or click Approve recipients then Send.'
                      : 'Send the email to checked recipients (all already approved).'
                  }
                >
                  <Send className="h-4 w-4" />
                  {sendMutation.isPending ? 'Sending…' : (testModeActive ? `Send to ${testRecipient || 'tester'}` : 'Send')}
                </button>
                <button
                  onClick={() => approveAndSendMutation.mutate()}
                  disabled={checkedEmails.length === 0 || approveAndSendMutation.isPending}
                  className={`px-4 py-1.5 text-sm font-medium rounded text-white inline-flex items-center gap-2 ${
                    testModeActive
                      ? 'bg-warning-600 hover:bg-warning-700'
                      : 'bg-primary-600 hover:bg-primary-700'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                  title="Approve recipients AND send the email in one click."
                >
                  <Send className="h-4 w-4" />
                  {approveAndSendMutation.isPending
                    ? 'Sending…'
                    : testModeActive
                      ? `Approve & send to ${testRecipient || 'tester'}`
                      : 'Approve & Send'}
                </button>
                {/* Schedule path — same recipient/edits snapshot the
                    operator just built in the drawer (including any
                    operator-override re-inclusions) but fires later via
                    pipeline_worker instead of inline. Closes the gap
                    where row-level quick-picks couldn't see drawer-state
                    overrides. */}
                <button
                  onClick={() => scheduleMutation.mutate(tomorrowAtSydney(10))}
                  disabled={checkedEmails.length === 0 || scheduleMutation.isPending}
                  className="px-3 py-1.5 text-sm border border-grey-300 rounded text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Schedule for tomorrow 10:00 AEST."
                >
                  Tomorrow 10am
                </button>
                <button
                  onClick={() => scheduleMutation.mutate(tomorrowAtSydney(14))}
                  disabled={checkedEmails.length === 0 || scheduleMutation.isPending}
                  className="px-3 py-1.5 text-sm border border-grey-300 rounded text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Schedule for tomorrow 14:00 AEST."
                >
                  Tomorrow 2pm
                </button>
                <div className="relative">
                  <button
                    onClick={() => setSchedulePickerOpen((v) => !v)}
                    disabled={checkedEmails.length === 0 || scheduleMutation.isPending}
                    className="px-3 py-1.5 text-sm border border-grey-300 rounded text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1"
                    title="Pick a specific date and time (Sydney TZ)."
                  >
                    <Clock className="h-4 w-4" />
                    {scheduleMutation.isPending ? 'Scheduling…' : 'Schedule…'}
                  </button>
                  {schedulePickerOpen && (
                    <ScheduleSendPopover
                      expiresAtUtcIso={draft?.expires_at ?? null}
                      submitting={scheduleMutation.isPending}
                      onSubmit={async (utcIso) => {
                        await scheduleMutation.mutateAsync(utcIso);
                        setSchedulePickerOpen(false);
                      }}
                      onClose={() => setSchedulePickerOpen(false)}
                    />
                  )}
                </div>
              </div>
            </div>

            {approveRecipientsMutation.isSuccess && approveRecipientsMutation.data?.ok && (
              <div className="mx-6 mb-4 rounded-lg border border-primary-200 bg-primary-50 p-3 text-sm text-primary-800">
                {approveRecipientsMutation.data.newly_approved_count > 0
                  ? `${approveRecipientsMutation.data.newly_approved_count} newly approved (${approveRecipientsMutation.data.approved_count} total). Draft stays in queue; click Send when ready.`
                  : `${approveRecipientsMutation.data.approved_count} recipients already approved. Click Send to fire the email.`}
              </div>
            )}
            {approveAndSendMutation.isError && (
              <div className="mx-6 mb-4 rounded-lg border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">
                Approve+Send failed: {(approveAndSendMutation.error as Error).message}
              </div>
            )}
            {approveAndSendMutation.isSuccess && approveAndSendMutation.data?.error && (
              <div className="mx-6 mb-4 rounded-lg border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">
                Server rejected: {approveAndSendMutation.data.error} — {approveAndSendMutation.data.error_detail}
              </div>
            )}
            {sendMutation.isError && (
              <div className="mx-6 mb-4 rounded-lg border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">
                Send failed: {(sendMutation.error as Error).message}
              </div>
            )}
            {sendMutation.isSuccess && sendMutation.data?.error && (
              <div className="mx-6 mb-4 rounded-lg border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">
                Server rejected: {sendMutation.data.error} — {sendMutation.data.error_detail}
              </div>
            )}
            {approveRecipientsMutation.isError && (
              <div className="mx-6 mb-4 rounded-lg border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">
                Approve recipients failed: {(approveRecipientsMutation.error as Error).message}
              </div>
            )}
            {scheduleMutation.isError && (
              <div className="mx-6 mb-4 rounded-lg border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">
                Schedule failed: {((scheduleMutation.error as any)?.response?.data?.detail ?? (scheduleMutation.error as Error).message)}
              </div>
            )}
          </>
        )}
      </div>

      {draft && (
        <AddRecipientModal
          isOpen={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          draftId={draft.draft_id}
          defaultOrganisation={draft.entity_display_name}
          onAdded={() => {
            qc.invalidateQueries({ queryKey: ['draft', draft.draft_id] });
            qc.invalidateQueries({ queryKey: ['queue'] });
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// Inline preview renderers — mirror publishing-service preview-renderer.ts
// ---------------------------------------------------------------------

function PreviewBody({
  mode,
  html,
  text,
}: { mode: PreviewMode; html: string; text: string }) {
  if (mode === 'plain_text') {
    return (
      <pre className="text-xs text-grey-800 whitespace-pre-wrap font-mono leading-relaxed">
        {text}
      </pre>
    );
  }

  let rendered = html;
  if (mode === 'images_blocked') {
    rendered = html.replace(/<img\b([^>]*)\/?>/gi, (_match, attrs: string) => {
      const altMatch = /alt=(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i.exec(attrs);
      const alt = altMatch ? (altMatch[1] ?? altMatch[2] ?? altMatch[3] ?? '') : '';
      const display = alt.trim() || '[image]';
      return `<span style="display:inline-block;padding:6px 10px;border:1px dashed #888;color:#555;font-size:12px;font-family:monospace;background:#fafafa;">🖼 ${escapeHtml(display)}</span>`;
    });
  } else if (mode === 'no_css') {
    rendered = html
      .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, '')
      .replace(/\sstyle="[^"]*"/gi, '')
      .replace(/\sstyle='[^']*'/gi, '');
  }

  // Render inside an iframe-style isolated container — using srcDoc would
  // sandbox better, but inline div renders fine for our locked template
  // and saves the operator a click to see content.
  return (
    <iframe
      title="Email preview"
      srcDoc={rendered}
      sandbox=""
      className="w-full h-[500px] bg-surface border-0 rounded"
    />
  );
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
